import { FC } from "react";
import Container from "react-bootstrap/Container";
import { FaEdit, FaFile, FaPlus, FaTrash } from "react-icons/fa";
import Alert from "react-bootstrap/esm/Alert";
import Card from "react-bootstrap/esm/Card";
import ButtonGroup from "react-bootstrap/esm/ButtonGroup";
import { useRouteParams } from "app/route/route";
import { NavigationScreen } from "app/component/screen/NavigationScreen";
import { RouteBreadcrumbs } from "app/route/RouteBreadcrumbs";
import {
    createReportVersionDeliverableFileRoute,
    deleteReportVersionDeliverableFileRoute,
    editReportVersionDeliverableFileRoute,
    reportVersionDeliverableFilesRoute,
} from "app/route/Routes";
import {
    ReportDeliverableFileFieldsFragment,
    ReportVersionStatus,
    useReportVersionDeliverableFilesQuery,
} from "app/api/graph/types";
import { reportVersionGraphId } from "app/api/graph/helpers";
import { LoadingScreen } from "app/component/screen/LoadingScreen";
import { ErrorScreen } from "app/component/screen/ErrorScreen";
import { HtmlContent } from "app/component/util/HtmlContent";
import { Toolbar, ToolbarButtons, ToolbarTitle } from "app/component/util/Toolbar";
import { ButtonLink } from "app/component/button/ButtonLink";
import { useReportVersionPermissions } from "app/api/graph/hook/use-report-version-permissions";
import { mapNodes } from "app/util/graph";
import { useGeneralPermissions } from "app/api/graph/hook/use-general-permissions";
import { DownloadReportVersionTestCertificatePdfButton } from "app/component/report-version/component/DownloadReportVersionTestCertificatePdfButton";
import { DownloadReportVersionPdfButton } from "app/component/report-version/component/DownloadReportVersionPdfButton";
import { DownloadReportVersionVulnerabilitiesSpreadsheetButton } from "app/component/report-version/component/DownloadReportVersionVulnerabilitiesSpreadsheetButton";
import { consultantRole, testerRole } from "app/api/graph/roles";
import { useAuth } from "app/api/AuthContext";

type FileCardProps = {
    reportId: number;
    versionId: number;
    file: ReportDeliverableFileFieldsFragment;
};

const FileCard = ({ reportId, versionId, file }: FileCardProps) => {
    const permissions = useGeneralPermissions(file.permissions);

    return (
        <Card key={file.id} className="mb-3">
            <Card.Header>
                <Toolbar>
                    <ToolbarTitle>{file.reportMediaObject.originalName}</ToolbarTitle>
                    <ToolbarButtons>
                        {permissions.canUpdate ? (
                            <ButtonLink
                                variant="warning"
                                to={editReportVersionDeliverableFileRoute.build({
                                    reportId,
                                    versionId,
                                    fileId: file._id,
                                })}
                            >
                                <FaEdit /> Edit
                            </ButtonLink>
                        ) : null}
                        {permissions.canDelete ? (
                            <ButtonLink
                                variant="danger"
                                to={deleteReportVersionDeliverableFileRoute.build({
                                    reportId,
                                    versionId,
                                    fileId: file._id,
                                })}
                            >
                                <FaTrash /> Delete
                            </ButtonLink>
                        ) : null}
                    </ToolbarButtons>
                </Toolbar>
            </Card.Header>
            <Card.Body>
                <figure className="figure">
                    <a href={file.reportMediaObject.contentUrl ?? ""} target="_blank" rel="noopener noreferrer">
                        {file.reportMediaObject.mimeType?.startsWith("image/") ? (
                            <img
                                className="figure-img img-fluid"
                                src={file.reportMediaObject.contentUrl ?? ""}
                                alt={file.reportMediaObject.originalName ?? "Screenshot"}
                            />
                        ) : (
                            <>
                                <FaFile />
                                {file.reportMediaObject.originalName}
                            </>
                        )}
                    </a>
                </figure>

                {file.description != null && <HtmlContent html={file.description} />}
            </Card.Body>
        </Card>
    );
};

export const ReportVersionDeliverableFilesScreen: FC = () => {
    const { reportId, versionId } = useRouteParams(reportVersionDeliverableFilesRoute);
    const { data, loading, error } = useReportVersionDeliverableFilesQuery({
        notifyOnNetworkStatusChange: true,
        variables: { versionId: reportVersionGraphId(versionId) },
    });
    const reportVersion = data?.reportVersion;
    const files = reportVersion?.deliverableFiles;
    const { canCreateDeliverableFile } = useReportVersionPermissions(reportVersion?.permissions);
    const { roles } = useAuth();

    if (loading && !files) {
        return <LoadingScreen />;
    }
    if (error) {
        return <ErrorScreen title="Error" message={error.message} />;
    }
    if (!files) {
        return <ErrorScreen title="Error" message="Not found." />;
    }
    if (
        reportVersion.status !== ReportVersionStatus.Published &&
        !roles?.includes(testerRole) &&
        !roles?.includes(consultantRole)
    ) {
        return (
            <Alert variant="danger">
                This report version hasn&lsquo;t been published yet, please check again later.
            </Alert>
        );
    }

    return (
        <NavigationScreen>
            <Container>
                <RouteBreadcrumbs route={reportVersionDeliverableFilesRoute} />

                <Toolbar>
                    <ToolbarTitle>
                        <h1>Deliverable files</h1>
                    </ToolbarTitle>

                    <ToolbarButtons>
                        <ButtonGroup>
                            <DownloadReportVersionPdfButton
                                versionId={reportVersion?._id}
                                title="Download report PDF"
                            />
                            <DownloadReportVersionTestCertificatePdfButton
                                title="Download test certificate PDF"
                                size="sm"
                                versionId={reportVersion._id}
                            />
                            <DownloadReportVersionVulnerabilitiesSpreadsheetButton versionId={reportVersion._id} />
                        </ButtonGroup>
                    </ToolbarButtons>
                </Toolbar>

                <Toolbar>
                    <ToolbarTitle>
                        <h2>Files</h2>
                    </ToolbarTitle>
                    <ToolbarButtons>
                        {canCreateDeliverableFile ? (
                            <ButtonLink
                                to={createReportVersionDeliverableFileRoute.build({
                                    reportId,
                                    versionId,
                                })}
                                variant="success"
                            >
                                <FaPlus /> New file
                            </ButtonLink>
                        ) : null}
                    </ToolbarButtons>
                </Toolbar>

                {files.edges?.length === 0 && <Alert variant="warning">No files have been provided.</Alert>}

                {mapNodes(files, (file) => (
                    <FileCard key={file.id} file={file} reportId={reportId} versionId={versionId} />
                ))}
            </Container>
        </NavigationScreen>
    );
};
